import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Button } from 'primereact/button';
import { ListBox } from 'primereact/listbox';
import _ from 'lodash';
import { Formik, Field, Form } from 'formik';
import * as yup from 'yup';
import { injectIntl } from 'react-intl';
import QuestionField from './QuestionField';
import AnswerField from './AnswerField';
import messages from '../intl-messages';
import { withGrowl } from '../../../../components/GrowlNotifications';


const questionValidationSchema = formatMessage => yup.object({
  question: yup.string()
    .max(250, formatMessage(messages.questionValidationMaxLength))
    .required(formatMessage(messages.questionValidationRequired)),
});

const answerValidationSchema = formatMessage => yup.object({
  answer: yup.string()
    .max(50, formatMessage(messages.answerValidationMaxLength))
    .required(formatMessage(messages.answerValidationRequired)),
});

const AnswersList = ({ answers, setAnswers, formatMessage }) => {
  const answerTemplate = option => (
    <Button
      label={option.label}
      className=".p-button-info"
      style={{ width: '100%' }}
      onClick={() => {
        const newAnswers = _.filter(answers, answer => answer.value !== option.value);
        setAnswers(newAnswers);
      }}
    />
  );


  return (
    <div>
      <div>
        <small>
          {formatMessage(messages.answerListLabelMessage)}
        </small>
      </div>
      <ListBox
        disabled
        options={answers}
        itemTemplate={answerTemplate}
        style={{ width: '100%' }}
      />
    </div>
  );
};

AnswersList.propTypes = {
  answers: PropTypes.arrayOf(PropTypes.any).isRequired,
  setAnswers: PropTypes.func.isRequired,
  formatMessage: PropTypes.func.isRequired,
};

const CreateDropdownField = ({
  addGlobalAriField, currentAccount, intl: { formatMessage }, growl,
}) => {
  const [answers, setAnswers] = useState([]);
  const maxAnswers = 500;

  return (
    <>
      <div className="p-col no-gutter">
        <Formik
          validationSchema={questionValidationSchema(formatMessage)}
          initialValues={{ question: '' }}
          onSubmit={(values, { resetForm, setSubmitting }) => {
            setSubmitting(true);

            const newField = {
              dataType: 'DROPDOWN',
              question: values.question,
              answers: answers.map(ans => ans.value),
            };
            addGlobalAriField({
              variables: {
                input: {
                  accountId: currentAccount,
                  fields: [newField],
                },
              },
            })
              .then(() => {
                setSubmitting(false);
                growl.show({
                  severity: 'success',
                  sticky: false,
                  summary: formatMessage(messages.growlAddFieldSuccessSummary),
                  detail: formatMessage(messages.growlAddDropdownFieldSuccessDetail),
                });
                setAnswers([]);
                resetForm();
              })
              .catch(() => {
                setSubmitting(false);
                growl.show({
                  severity: 'error',
                  sticky: true,
                  summary: formatMessage(messages.growlAddFieldFailSummary),
                  detail: formatMessage(messages.growlAddDropdownFieldFailDetail),
                });
              });
          }}
        >
          {({ isValid, isSubmitting }) => (
            <Form>
              <div className="p-col-12">
                <Field
                  component={QuestionField}
                  name="question"
                  rows={5}
                  cols={25}
                  placeholder={formatMessage(messages.enterQuestionPlaceholder)}
                />
              </div>

              <div className="p-col-12">
                <Button
                  type="submit"
                  label={formatMessage(messages.submitLabel)}
                  disabled={!isValid || isSubmitting || answers.length === 0}
                />
              </div>
            </Form>
          )}
        </Formik>
      </div>
      <div className="p-col">
        <Formik
          validationSchema={answerValidationSchema(formatMessage)}
          initialValues={{ answer: '' }}
          onSubmit={(values, { resetForm }) => {
            const duplicateAnswer = _.includes(answers.map(ans => ans.value), values.answer);
            if (!duplicateAnswer) {
              setAnswers([...answers, {
                label: values.answer, value: values.answer, key: values.answer,
              }]);
              resetForm();
            }
          }}
        >
          {() => (
            <Form>
              {
                answers.length < maxAnswers && (
                  <div className="p-col-12">
                    <Field
                      name="answer"
                      component={AnswerField}
                      placeholder={formatMessage(messages.answerFieldPlaceholder)}
                    />
                    <Button
                      type="submit"
                      label={formatMessage(messages.addAnswerBtnLabel)}
                      className="p-button-secondary"
                      style={{ marginTop: '5px' }}
                    />
                  </div>
                )
              }
              {answers.length === 0 && (
                <div style={{ marginLeft: '10px' }}>
                  <small>
                    {formatMessage(messages.answerListEmptyMessage)}
                  </small>
                </div>
              )}
              {answers.length > 0 && (
                <AnswersList
                  answers={answers}
                  setAnswers={setAnswers}
                  formatMessage={formatMessage}
                />
              )}
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
};

CreateDropdownField.propTypes = {
  addGlobalAriField: PropTypes.func.isRequired,
  currentAccount: PropTypes.string.isRequired,
  intl: PropTypes.objectOf(PropTypes.any).isRequired,
  growl: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default injectIntl(withGrowl(CreateDropdownField));
